'use client';

/**
 * WorkflowFlowView — Horizontal drag-and-drop flow designer with branching
 *
 * Renders steps as a horizontal process flow with:
 *  - Drag-and-drop reordering via @dnd-kit
 *  - Decision/condition steps show Yes/No branch paths
 *  - Inline step config preview with expand/collapse
 *  - Start/End nodes and arrow connectors
 */

import React, { useState, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { WorkflowStep, StepType } from '@/services/api/workflows';
import { getStepTypeFromConfig } from './StepTypePicker';
import {
  GripVertical,
  Edit,
  Trash2,
  Plus,
  ArrowRight,
  CheckCircle,
  Clock,
  AlertCircle,
  GitBranch,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Mail,
  Phone,
  Bot,
  FileText,
  ClipboardList,
  Users,
  Zap,
  Play,
} from 'lucide-react';

// Re-export FlowStep for backwards compat (used by old callers)
export interface FlowStep {
  name: string;
  step_type: string;
  order: number;
  config: Record<string, any>;
  form_template_id?: number | null;
  is_critical?: boolean;
}

// ============================================================================
// HELPERS
// ============================================================================

const STEP_COLORS: Record<string, { bg: string; border: string; accent: string }> = {
  task: { bg: '#dbeafe', border: '#3b82f6', accent: '#1d4ed8' },
  notification: { bg: '#fef3c7', border: '#f59e0b', accent: '#d97706' },
  communication: { bg: '#cffafe', border: '#06b6d4', accent: '#0891b2' },
  wait_for_response: { bg: '#fef3c7', border: '#f59e0b', accent: '#d97706' },
  approval: { bg: '#dcfce7', border: '#22c55e', accent: '#16a34a' },
  condition: { bg: '#f3e8ff', border: '#a855f7', accent: '#9333ea' },
  decision: { bg: '#f3e8ff', border: '#a855f7', accent: '#9333ea' },
  api_call: { bg: '#ffedd5', border: '#f97316', accent: '#ea580c' },
  wait: { bg: '#f3f4f6', border: '#6b7280', accent: '#4b5563' },
  form: { bg: '#e0e7ff', border: '#6366f1', accent: '#4f46e5' },
  billing: { bg: '#d1fae5', border: '#10b981', accent: '#059669' },
  pricing_agent: { bg: '#ede9fe', border: '#8b5cf6', accent: '#7c3aed' },
};

const STEP_ICONS: Record<string, React.ReactNode> = {
  communication: <MessageSquare className="w-4 h-4" />,
  notification: <Zap className="w-4 h-4" />,
  task: <CheckCircle className="w-4 h-4" />,
  wait: <Clock className="w-4 h-4" />,
  wait_for_response: <Clock className="w-4 h-4" />,
  approval: <Users className="w-4 h-4" />,
  condition: <GitBranch className="w-4 h-4" />,
  decision: <GitBranch className="w-4 h-4" />,
  form: <ClipboardList className="w-4 h-4" />,
  billing: <FileText className="w-4 h-4" />,
  api_call: <Zap className="w-4 h-4" />,
  pricing_agent: <Bot className="w-4 h-4" />,
};

function getColors(type: string) {
  return STEP_COLORS[type] || STEP_COLORS.task;
}

function isBranchStep(step: WorkflowStep): boolean {
  return (
    step.step_type === 'condition' ||
    step.step_type === ('decision' as StepType) ||
    !!(step.step_config?.true_branch_step && step.step_config?.false_branch_step)
  );
}

function formatConfigEntries(config: Record<string, any>): { label: string; value: string }[] {
  if (!config || typeof config !== 'object') return [];
  const skip = new Set(['true_branch_step', 'false_branch_step', 'branching_mode', 'condition_field', 'condition_operator', 'condition_value']);
  return Object.entries(config)
    .filter(([k, v]) => !skip.has(k) && v !== null && v !== undefined && v !== '')
    .map(([k, v]) => ({
      label: k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      value: typeof v === 'boolean' ? (v ? 'Yes' : 'No') :
             Array.isArray(v) ? v.join(', ') :
             typeof v === 'object' ? JSON.stringify(v) :
             String(v).length > 40 ? String(v).slice(0, 37) + '...' : String(v),
    }));
}

// ============================================================================
// SORTABLE FLOW NODE
// ============================================================================

const SortableFlowNode: React.FC<{
  step: WorkflowStep;
  allSteps: WorkflowStep[];
  onEdit: (step: WorkflowStep) => void;
  onDelete: (step: WorkflowStep) => void;
  isLast: boolean;
}> = ({ step, allSteps, onEdit, onDelete, isLast }) => {
  const [expanded, setExpanded] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const stepTypeOption = getStepTypeFromConfig(step.step_type, step.step_config);
  const stepLabel = stepTypeOption?.label || step.step_type.replace(/_/g, ' ');
  const colors = getColors(step.step_type);
  const configEntries = formatConfigEntries(step.step_config || {});
  const isBranch = isBranchStep(step);
  const trueStep = isBranch ? allSteps.find(s => s.step_order === step.step_config?.true_branch_step) : null;
  const falseStep = isBranch ? allSteps.find(s => s.step_order === step.step_config?.false_branch_step) : null;

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Main card */}
        <div
          ref={setNodeRef}
          style={{
            ...style,
            backgroundColor: colors.bg,
            border: `2px solid ${colors.border}`,
            borderRadius: isBranch ? '16px' : '12px',
            padding: '14px 16px',
            minWidth: '240px',
            maxWidth: '320px',
            cursor: isDragging ? 'grabbing' : 'default',
            boxShadow: isDragging ? '0 10px 25px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.08)',
            position: 'relative',
          }}
        >
          {/* Drag handle */}
          <div
            {...attributes}
            {...listeners}
            style={{
              position: 'absolute',
              top: '8px',
              left: '8px',
              cursor: 'grab',
              padding: '4px',
              borderRadius: '4px',
              backgroundColor: 'rgba(255,255,255,0.7)',
            }}
          >
            <GripVertical className="w-4 h-4" style={{ color: '#9ca3af' }} />
          </div>

          {/* Step number */}
          <div style={{
            position: 'absolute',
            top: '-10px',
            right: '-10px',
            backgroundColor: colors.accent,
            color: 'white',
            borderRadius: '50%',
            width: '28px',
            height: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            fontWeight: 700,
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }}>
            {step.step_order}
          </div>

          <div style={{ marginLeft: '24px' }}>
            {/* Type row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
              <span style={{ color: colors.accent }}>
                {STEP_ICONS[step.step_type] || <Play className="w-4 h-4" />}
              </span>
              <span style={{
                fontSize: '11px',
                fontWeight: 600,
                color: colors.accent,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}>
                {stepLabel}
              </span>
              {isBranch && (
                <span style={{
                  fontSize: '9px',
                  fontWeight: 700,
                  color: '#9333ea',
                  backgroundColor: '#f3e8ff',
                  padding: '1px 6px',
                  borderRadius: '8px',
                  border: '1px solid #d8b4fe',
                }}>
                  DECISION
                </span>
              )}
            </div>

            {/* Name */}
            <div style={{ fontWeight: 600, fontSize: '14px', color: '#1f2937', marginBottom: '6px', lineHeight: '1.3' }}>
              {step.step_name}
            </div>

            {/* Required */}
            {step.required && (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 8px',
                backgroundColor: '#fef2f2',
                border: '1px solid #fca5a5',
                borderRadius: '12px',
                fontSize: '10px',
                color: '#dc2626',
                fontWeight: 500,
                marginBottom: '6px',
              }}>
                <AlertCircle className="w-3 h-3" />
                Required
              </div>
            )}

            {/* Condition details */}
            {isBranch && step.step_config?.condition_field && (
              <div style={{
                marginTop: '4px',
                marginBottom: '6px',
                padding: '4px 8px',
                backgroundColor: 'rgba(255,255,255,0.7)',
                borderRadius: '6px',
                fontSize: '10px',
                color: '#6b7280',
                fontStyle: 'italic',
              }}>
                If <strong>{step.step_config.condition_field}</strong>{' '}
                {step.step_config.condition_operator || '='}{' '}
                <strong>{step.step_config.condition_value || '...'}</strong>
              </div>
            )}

            {/* Inline config */}
            {configEntries.length > 0 && (
              <div style={{
                marginTop: '6px',
                padding: '8px',
                backgroundColor: 'rgba(255,255,255,0.6)',
                borderRadius: '6px',
                fontSize: '11px',
                color: '#4b5563',
              }}>
                {configEntries.slice(0, expanded ? configEntries.length : 2).map((entry, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px', gap: '8px' }}>
                    <span style={{ opacity: 0.7, whiteSpace: 'nowrap' }}>{entry.label}:</span>
                    <span style={{ fontWeight: 500, textAlign: 'right', wordBreak: 'break-word' }}>{entry.value}</span>
                  </div>
                ))}
                {configEntries.length > 2 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      width: '100%',
                      marginTop: '4px',
                      padding: '2px 0',
                      border: 'none',
                      background: 'none',
                      color: colors.accent,
                      fontSize: '10px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {expanded
                      ? <><ChevronUp className="w-3 h-3" /> Show less</>
                      : <><ChevronDown className="w-3 h-3" /> +{configEntries.length - 2} more</>
                    }
                  </button>
                )}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '6px', marginTop: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => onEdit(step)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px',
                  backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px',
                  fontSize: '12px', color: '#374151', cursor: 'pointer', transition: 'all 0.15s',
                }}
                onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; }}
                onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
              >
                <Edit className="w-3 h-3" /> Edit
              </button>
              <button
                onClick={() => onDelete(step)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px',
                  backgroundColor: 'white', border: '1px solid #fca5a5', borderRadius: '6px',
                  fontSize: '12px', color: '#dc2626', cursor: 'pointer', transition: 'all 0.15s',
                }}
                onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#fef2f2'; }}
                onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>

        {/* Branch paths below the card for decision steps */}
        {isBranch && (trueStep || falseStep) && (
          <div style={{ display: 'flex', gap: '12px', marginTop: '10px' }}>
            {/* Yes path */}
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '110px',
            }}>
              <div style={{
                width: '2px', height: '12px', backgroundColor: '#22c55e',
              }} />
              <span style={{
                fontSize: '9px', fontWeight: 700, color: '#16a34a',
                backgroundColor: '#dcfce7', border: '1px solid #86efac',
                padding: '1px 8px', borderRadius: '8px', marginBottom: '4px',
              }}>YES</span>
              <div style={{
                padding: '6px 10px',
                backgroundColor: '#dcfce7',
                border: '1.5px solid #86efac',
                borderRadius: '8px',
                fontSize: '11px',
                color: '#15803d',
                fontWeight: 500,
                textAlign: 'center',
                maxWidth: '140px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {trueStep ? `→ ${trueStep.step_name}` : '→ Next'}
              </div>
            </div>
            {/* No path */}
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '110px',
            }}>
              <div style={{
                width: '2px', height: '12px', backgroundColor: '#ef4444',
              }} />
              <span style={{
                fontSize: '9px', fontWeight: 700, color: '#dc2626',
                backgroundColor: '#fef2f2', border: '1px solid #fca5a5',
                padding: '1px 8px', borderRadius: '8px', marginBottom: '4px',
              }}>NO</span>
              <div style={{
                padding: '6px 10px',
                backgroundColor: falseStep ? '#fef2f2' : '#f3f4f6',
                border: `1.5px ${falseStep ? 'solid #fca5a5' : 'dashed #d1d5db'}`,
                borderRadius: '8px',
                fontSize: '11px',
                color: falseStep ? '#b91c1c' : '#6b7280',
                fontWeight: 500,
                textAlign: 'center',
                maxWidth: '140px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontStyle: falseStep ? 'normal' : 'italic',
              }}>
                {falseStep ? `→ ${falseStep.step_name}` : 'End'}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Arrow connector to next step */}
      {!isLast && (
        <div style={{ display: 'flex', alignItems: 'center', padding: '0 8px', alignSelf: 'center' }}>
          <div style={{ width: '40px', height: '2px', backgroundColor: '#d1d5db', position: 'relative' }}>
            <ArrowRight
              className="w-5 h-5"
              style={{
                position: 'absolute', right: '-8px', top: '50%', transform: 'translateY(-50%)',
                color: '#9ca3af',
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface WorkflowFlowViewProps {
  steps: WorkflowStep[];
  onEdit: (step: WorkflowStep) => void;
  onDelete: (step: WorkflowStep) => void;
  onAddStep: () => void;
  onReorder: (reorderedSteps: WorkflowStep[]) => void;
  loading?: boolean;
  className?: string;
}

export function WorkflowFlowView({
  steps,
  onEdit,
  onDelete,
  onAddStep,
  onReorder,
  loading,
  className = '',
}: WorkflowFlowViewProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = steps.findIndex((s) => s.id === active.id);
      const newIndex = steps.findIndex((s) => s.id === over.id);
      const reordered = arrayMove(steps, oldIndex, newIndex).map((step, idx) => ({
        ...step,
        step_order: idx + 1,
      }));
      onReorder(reordered);
    }
  };

  const hasBranches = steps.some(s => isBranchStep(s));

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '0 auto 16px' }}></div>
          <p style={{ color: '#6b7280' }}>Loading workflow steps...</p>
        </div>
      </div>
    );
  }

  if (steps.length === 0) {
    return (
      <div className={className} style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '60px', backgroundColor: '#f9fafb', borderRadius: '12px', border: '2px dashed #d1d5db',
      }}>
        <div style={{
          width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#e5e7eb',
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px',
        }}>
          <GitBranch className="w-8 h-8" style={{ color: '#9ca3af' }} />
        </div>
        <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
          No Steps Defined
        </h3>
        <p style={{ color: '#6b7280', marginBottom: '20px' }}>
          Start building your workflow by adding the first step
        </p>
        <button
          onClick={onAddStep}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px',
            backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '8px',
            fontSize: '14px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
          }}
          onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#059669'; }}
          onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#10b981'; }}
        >
          <Plus className="w-5 h-5" /> Add First Step
        </button>
      </div>
    );
  }

  return (
    <div className={className} style={{ backgroundColor: '#f9fafb', borderRadius: '12px', padding: '24px', overflowX: 'auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#374151', margin: 0 }}>
            Workflow Flow
          </h3>
          <p style={{ fontSize: '13px', color: '#6b7280', margin: '4px 0 0 0' }}>
            Drag steps to reorder &bull; {steps.length} {steps.length !== 1 ? 'steps' : 'step'}
            {hasBranches && ' \u00b7 Contains decision points'}
          </p>
        </div>
        {hasBranches && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '11px', color: '#6b7280' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#a855f7' }} />
              Decision
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#22c55e' }} />
              Yes
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#ef4444' }} />
              No
            </span>
          </div>
        )}
      </div>

      {/* Horizontal flow */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={steps.map((s) => s.id)} strategy={horizontalListSortingStrategy}>
          <div style={{
            display: 'flex', alignItems: 'flex-start', padding: '20px 0', minWidth: 'max-content',
          }}>
            {/* Start */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginRight: '16px', alignSelf: 'center' }}>
              <div style={{
                width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#22c55e',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(34, 197, 94, 0.3)',
              }}>
                <Play className="w-6 h-6" style={{ color: 'white' }} />
              </div>
              <span style={{ marginTop: '8px', fontSize: '11px', fontWeight: 600, color: '#22c55e' }}>START</span>
            </div>

            <div style={{ width: '24px', height: '2px', backgroundColor: '#22c55e', marginRight: '8px', alignSelf: 'center' }} />

            {/* Steps */}
            {steps.map((step, index) => (
              <SortableFlowNode
                key={step.id}
                step={step}
                allSteps={steps}
                onEdit={onEdit}
                onDelete={onDelete}
                isLast={index === steps.length - 1}
              />
            ))}

            <div style={{ width: '24px', height: '2px', backgroundColor: '#d1d5db', marginLeft: '8px', alignSelf: 'center' }} />

            {/* End */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginLeft: '16px', alignSelf: 'center' }}>
              <div style={{
                width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#6b7280',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(107, 114, 128, 0.3)',
              }}>
                <CheckCircle className="w-6 h-6" style={{ color: 'white' }} />
              </div>
              <span style={{ marginTop: '8px', fontSize: '11px', fontWeight: 600, color: '#6b7280' }}>END</span>
            </div>

            {/* Add button */}
            <div style={{ marginLeft: '24px', alignSelf: 'center' }}>
              <button
                onClick={onAddStep}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  padding: '16px 24px', backgroundColor: 'white', border: '2px dashed #d1d5db',
                  borderRadius: '12px', cursor: 'pointer', transition: 'all 0.15s', minWidth: '120px',
                }}
                onMouseOver={(e) => { e.currentTarget.style.borderColor = '#10b981'; e.currentTarget.style.backgroundColor = '#f0fdf4'; }}
                onMouseOut={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.backgroundColor = 'white'; }}
              >
                <Plus className="w-6 h-6" style={{ color: '#10b981', marginBottom: '4px' }} />
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#10b981' }}>Add Step</span>
              </button>
            </div>
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

export default WorkflowFlowView;
